export const PER_PAGE = 9

export interface Pagination {
  basePath: string
  page: number
  pageCount: number
}

/** Page 1 of an archive is its base path, page n is `{basePath}/page/{n}`. */
export const pagePath = (basePath: string, page: number) =>
  page === 1 ? basePath : `${basePath}/page/${page}`

const pageCount = (itemCount: number) => Math.max(1, Math.ceil(itemCount / PER_PAGE))

/** The items of one page, or null if the archive has no such page. */
export const paginate = <T>(items: T[], page: number, basePath: string) => {
  const pagination: Pagination = { basePath, page, pageCount: pageCount(items.length) }
  if (page < 1 || page > pagination.pageCount) {
    return null
  }
  return { items: items.slice((page - 1) * PER_PAGE, page * PER_PAGE), pagination }
}

/** The `n` params of the `/page/[n]` routes. Page 1 has no such route. */
export const pageParams = ({ pageCount }: Pagination) =>
  Array.from({ length: pageCount - 1 }, (_, i) => String(i + 2))

/** Page number of a `/page/[n]` route. Null for page 1, which lives at the base path, and for non-canonical numbers like `02`. */
export const parsePageParam = (n: string | undefined) =>
  n && /^[1-9]\d*$/.test(n) && n !== "1" ? Number(n) : null

/** Page numbers to link, with null for a gap: the first, the last and two around the current one. */
export const visiblePages = ({ page, pageCount }: Pagination): (number | null)[] => {
  const pages: (number | null)[] = []
  for (let n = 1; n <= pageCount; n++) {
    if (n === 1 || n === pageCount || Math.abs(n - page) <= 2) {
      pages.push(n)
    } else if (pages.at(-1) !== null) {
      pages.push(null)
    }
  }
  return pages
}
